import { Button } from "@/components/ui/button";
import { fmtUsd } from "@/lib/api";
import { Lightning, Play, Pause, StopCircle, Warning, Wallet, SignOut, Power } from "@phosphor-icons/react";

const statusStyles = {
  running: { label: "RUNNING", dot: "bg-green-500", text: "text-green-400" },
  paused: { label: "PAUSED", dot: "bg-yellow-500", text: "text-yellow-400" },
  stopped: { label: "STOPPED", dot: "bg-zinc-500", text: "text-zinc-400" },
};

export default function TopBar({ state, stats, mode, onStart, onPause, onResume, onStop, onEmergency, onLogout }) {
  const st = statusStyles[state?.status] || statusStyles.stopped;
  const running = state?.status === "running";
  const paused = state?.status === "paused";
  const pnl = stats?.daily_pnl ?? 0;
  const pnlPos = pnl >= 0;

  return (
    <header className="h-14 border-b border-[#27272a] flex items-center justify-between px-4 shrink-0 bg-[#09090b]">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2.5">
          <div className="h-7 w-7 bg-cyan-500 flex items-center justify-center">
            <Lightning weight="fill" className="text-black" size={17} />
          </div>
          <span className="font-head text-sm font-semibold tracking-tight hidden sm:inline">MULTICHAIN <span className="text-cyan-400">SNIPER</span></span>
        </div>
        <div className="h-6 w-px bg-[#27272a]" />
        <div className="flex items-center gap-2" data-testid="bot-status">
          <span className={`h-2 w-2 rounded-full ${st.dot} ${running ? "pulse-dot" : ""}`} />
          <span className={`text-xs font-mono font-medium ${st.text}`}>{st.label}</span>
        </div>
        <div className={`text-[10px] font-mono px-2 py-0.5 border ${mode === "live" ? "border-red-500/50 text-red-400" : "border-cyan-500/40 text-cyan-400"}`}
          data-testid="mode-badge">
          {mode === "live" ? "LIVE (SIM)" : "PAPER"}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="hidden md:flex items-center gap-2 text-xs" data-testid="wallet-status">
          <Wallet size={16} className="text-[#a1a1aa]" />
          <span className="font-mono text-[#f4f4f5]">{fmtUsd(stats?.balance)}</span>
        </div>
        <div className="h-6 w-px bg-[#27272a] hidden md:block" />
        <div className="flex flex-col items-end" data-testid="daily-pnl">
          <span className="text-[9px] uppercase tracking-wide text-[#a1a1aa]">Daily PnL</span>
          <span className={`font-mono text-sm font-semibold ${pnlPos ? "text-green-400" : "text-red-400"}`}>
            {pnlPos ? "+" : ""}{fmtUsd(pnl)}
          </span>
        </div>

        <div className="flex items-center gap-1.5 ml-2">
          {!running && !paused && (
            <Button data-testid="bot-start-btn" size="sm" onClick={onStart}
              className="rounded-none h-8 bg-green-600 hover:bg-green-500 text-white text-xs font-medium active:scale-95">
              <Play weight="fill" size={14} className="mr-1" /> Start
            </Button>
          )}
          {running && (
            <Button data-testid="bot-pause-btn" size="sm" onClick={onPause}
              className="rounded-none h-8 bg-yellow-600 hover:bg-yellow-500 text-black text-xs font-medium active:scale-95">
              <Pause weight="fill" size={14} className="mr-1" /> Pause
            </Button>
          )}
          {paused && (
            <Button data-testid="bot-resume-btn" size="sm" onClick={onResume}
              className="rounded-none h-8 bg-green-600 hover:bg-green-500 text-white text-xs font-medium active:scale-95">
              <Play weight="fill" size={14} className="mr-1" /> Resume
            </Button>
          )}
          {(running || paused) && (
            <Button data-testid="bot-stop-btn" size="sm" variant="outline" onClick={onStop}
              className="rounded-none h-8 border-[#3f3f46] bg-transparent hover:bg-[#27272a] text-xs active:scale-95">
              <StopCircle size={14} className="mr-1" /> Stop
            </Button>
          )}
          <Button data-testid="emergency-stop-btn" size="sm" onClick={onEmergency}
            className="rounded-none h-8 bg-red-600 hover:bg-red-500 text-white text-xs font-semibold active:scale-95 ml-1">
            <Warning weight="fill" size={14} className="mr-1" /> EMERGENCY
          </Button>
          <button data-testid="logout-btn" onClick={onLogout} title="Logout"
            className="ml-1 h-8 w-8 flex items-center justify-center border border-[#27272a] hover:bg-[#27272a] transition-colors">
            <SignOut size={15} className="text-[#a1a1aa]" />
          </button>
        </div>
      </div>
    </header>
  );
}
